import React, { useState, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Input,
  useToast,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon
} from '@chakra-ui/react';
import { FaUpload, FaFileCsv, FaTimes, FaEye } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

const LabTestMenuUpload = ({ labId, labName }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef();
  const toast = useToast();

  // File validation
  const validateFile = (file) => {
    // Allow all file types including PDFs
    const maxSize = 50 * 1024 * 1024; // 50MB limit for larger files like PDFs
    
    if (file.size > maxSize) {
      throw new Error('File size must be less than 50MB');
    }
    
    return true;
  };

  // Generate unique filename
  const generateFileName = (originalName) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const extension = originalName.split('.').pop();
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
    return `${labId}_${nameWithoutExt}_${timestamp}.${extension}`;
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    try {
      validateFile(selectedFile);
      setFile(selectedFile);
      setUploadStatus('idle');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message);
      setUploadStatus('error');
      toast({
        title: 'Invalid File',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      toast({
        title: 'No File Selected',
        description: 'Please select a file to upload',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);

    try {
      // Generate unique filename
      const fileName = generateFileName(file.name);
      const filePath = `lab-uploads/${labId}/${fileName}`;

      // Upload file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('lab-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('lab-files')
        .getPublicUrl(filePath);

      // Record upload in labs table (for lab portal display)
      const { data, error } = await supabase
        .from('labs')
        .update({
          test_menu_file: urlData.publicUrl,
          test_menu_filename: file.name,
          test_menu_uploaded_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', labId)
        .select()
        .single();

      if (error) {
        console.warn('Labs table update failed:', error);
        // Don't throw here, file upload was successful
      }

      // Also record upload in test_directory_uploads table (for admin tracking)
      const { error: uploadRecordError } = await supabase
        .from('test_directory_uploads')
        .insert({
          lab_id: labId,
          lab_name: labName || 'Unknown Lab',
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || file.name.split('.').pop() || 'unknown',
          file_url: urlData.publicUrl,
          upload_status: 'pending_review',
          uploaded_at: new Date().toISOString()
        });

      if (uploadRecordError) {
        console.warn('test_directory_uploads insert failed:', uploadRecordError);
        // Don't throw here, file upload was successful, just admin tracking failed
      }

      setUploadProgress(100);
      setUploadStatus('success');
      setUploadedFile({
        fileName: file.name,
        fileUrl: urlData.publicUrl,
        fileSize: file.size
      });

      toast({
        title: 'Upload Successful',
        description: 'Your test menu file has been uploaded successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      const errorMsg = error.message || error.details || 'There was an error uploading your file. Please try again.';
      setErrorMessage(errorMsg);
      
      toast({
        title: 'Upload Failed',
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };


  // Reset form
  const resetForm = () => {
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setUploadStatus('idle');
    setErrorMessage('');
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <Heading size="md">Upload Test Menu</Heading>
        <Text fontSize="sm" color="gray.600">
          Upload your lab's test menu in any format (PDF, Excel, CSV, etc.)
        </Text>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* File Requirements */}
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>File Requirements</AlertTitle>
              <AlertDescription>
                <Text fontSize="sm">
                  • <strong>Supported formats:</strong> PDF, Excel, CSV, Word, Images, and more<br/>
                  • <strong>Maximum size:</strong> 50MB<br/>
                  • <strong>Note:</strong> Files will be reviewed before being published to the directory
                </Text>
              </AlertDescription>
            </Box>
          </Alert>

          {/* File Input */}
          <Box>
            <Input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
              mb={2}
            />
            {file && (
              <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
                <Icon as={FaFileCsv} color="blue.500" />
                <Text fontSize="sm" fontWeight="medium">{file.name}</Text>
                <Text fontSize="xs" color="gray.500">({(file.size / 1024).toFixed(1)} KB)</Text>
                <Button size="xs" variant="ghost" onClick={resetForm}>
                  <Icon as={FaTimes} />
                </Button>
              </HStack>
            )}
          </Box>

          {/* Upload Progress */}
          {uploading && (
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="blue.600">Uploading...</Text>
                <Text fontSize="sm" color="blue.600">{uploadProgress}%</Text>
              </HStack>
              <Progress value={uploadProgress} colorScheme="blue" size="sm" />
            </Box>
          )}

          {/* Error Message */}
          {uploadStatus === 'error' && errorMessage && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Success Message */}
          {uploadStatus === 'success' && uploadedFile && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Upload Complete!</AlertTitle>
                <AlertDescription>
                  Your test menu file "{uploadedFile.fileName}" has been uploaded successfully and is pending review.
                  {uploadedFile.fileUrl && (
                    <Button size="sm" variant="link" ml={2} onClick={() => window.open(uploadedFile.fileUrl, '_blank')}>
                      View File
                    </Button>
                  )}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Action Buttons */}
          <HStack spacing={3}>
            <Button
              colorScheme="blue"
              leftIcon={<Icon as={FaUpload} />}
              onClick={handleUpload}
              isLoading={uploading}
              loadingText="Uploading..."
              isDisabled={!file || uploading}
            >
              Upload File
            </Button>
            
            {uploadStatus === 'success' && uploadedFile?.fileUrl && (
              <Button
                variant="outline"
                leftIcon={<Icon as={FaEye} />}
                onClick={() => window.open(uploadedFile.fileUrl, '_blank')}
              >
                View File
              </Button>
            )}
            
            <Button
              variant="ghost"
              onClick={resetForm}
              isDisabled={uploading}
            >
              Reset
            </Button>
          </HStack>

          {/* Upload Info */}
          <Box p={4} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" fontWeight="bold" mb={2}>Upload Information:</Text>
            <Text fontSize="xs" color="gray.600">
              • Files are stored securely and will be reviewed by our team<br/>
              • You can upload PDFs, Excel files, CSV files, or any other format<br/>
              • Once approved, your test menu will be visible to patients and providers
            </Text>
          </Box>
        </VStack>
      </CardBody>

    </Card>
  );
};

export default LabTestMenuUpload;